import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, FileText, Warning, ArrowRight, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const LEASE_TYPES = ["Property", "Vehicle", "Equipment", "IT & Software", "Machinery", "Other"];

const EMPTY = {
  title: "", asset_id: "", supplier_id: "none", lease_type: "Property", party_role: "Lessee", lease_nature: "Expense",
  start_date: "", end_date: "", rent_amount: "", frequency: "Monthly", currency: "USD",
  discount_rate: "5", advance_payment: "0", payment_timing: "arrears",
  is_low_value: false, lessor_classification: "Finance", notes: "",
};

export default function Leases() {
  const [leases, setLeases] = useState([]);
  const [assets, setAssets] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [fType, setFType] = useState("all");
  const [fAsset, setFAsset] = useState("all");
  const [fNature, setFNature] = useState("all");
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (l) => {
    setEditingId(l.id);
    setForm({
      title: l.title, asset_id: l.asset_id, supplier_id: l.supplier_id || "none",
      lease_type: l.lease_type, party_role: l.party_role, lease_nature: l.lease_nature || "Expense",
      start_date: l.start_date, end_date: l.end_date, rent_amount: String(l.rent_amount),
      frequency: l.frequency, currency: l.currency, discount_rate: String(l.discount_rate),
      advance_payment: String(l.advance_payment), payment_timing: l.payment_timing,
      is_low_value: l.is_low_value, lessor_classification: l.lessor_classification || "Finance",
      notes: l.notes || "",
    });
    setOpen(true);
  };

  const load = useCallback(() => {
    const params = {};
    if (fType !== "all") params.lease_type = fType;
    if (fAsset !== "all") params.asset_id = fAsset;
    if (fNature !== "all") params.lease_nature = fNature;
    api.get("/leases", { params }).then((r) => setLeases(r.data));
  }, [fType, fAsset, fNature]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data));
  }, []);
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId) {
      api.get(`/leases/${editId}`).then((r) => openEdit(r.data)).catch(() => {});
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.asset_id) { toast.error("Select an asset"); return; }
    if (!form.start_date || !form.end_date) { toast.error("Start and end dates required"); return; }
    try {
      const payload = {
        ...form,
        supplier_id: form.supplier_id === "none" ? null : form.supplier_id,
        rent_amount: parseFloat(form.rent_amount) || 0,
        discount_rate: parseFloat(form.discount_rate) || 0,
        advance_payment: parseFloat(form.advance_payment) || 0,
      };
      if (editingId) {
        await api.put(`/leases/${editingId}`, payload);
        toast.success("Lease updated");
      } else {
        await api.post("/leases", payload);
        toast.success("Lease created");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between fade-up">
        <div>
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">Contracts</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Leases</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button className="rounded-sm gap-2" data-testid="add-lease-btn" onClick={openCreate}><Plus size={16} /> New Lease</Button>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{editingId ? "Edit Lease Contract" : "New Lease Contract"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Lease Title</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5 rounded-sm" data-testid="lease-title" placeholder="HQ Office Lease 2025" />
              </div>
              <div>
                <Label>Asset</Label>
                <Select value={form.asset_id} onValueChange={(v) => set("asset_id", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-asset"><SelectValue placeholder="Select asset" /></SelectTrigger>
                  <SelectContent>
                    {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_number} — {a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lease Type</Label>
                <Select value={form.lease_type} onValueChange={(v) => set("lease_type", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEASE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Supplier / Lessor (for payments)</Label>
                <Select value={form.supplier_id} onValueChange={(v) => set("supplier_id", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No supplier</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.party_role} onValueChange={(v) => set("party_role", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Lessee">Lessee</SelectItem><SelectItem value="Lessor">Lessor</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lease Nature</Label>
                <Select value={form.lease_nature} onValueChange={(v) => set("lease_nature", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-nature"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Expense">Expense Lease (you pay)</SelectItem>
                    <SelectItem value="Revenue">Revenue Lease (you receive)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.party_role === "Lessor" && (
                <div>
                  <Label>Lessor Classification</Label>
                  <Select value={form.lessor_classification} onValueChange={(v) => set("lessor_classification", v)}>
                    <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lessor-class"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Finance">Finance Lease</SelectItem><SelectItem value="Operating">Operating Lease</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="lease-start" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="lease-end" />
              </div>
              <div>
                <Label>Rent Amount (per period)</Label>
                <Input type="number" value={form.rent_amount} onChange={(e) => set("rent_amount", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="lease-rent" />
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem>
                    <SelectItem value="Yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Discount Rate (annual %)</Label>
                <Input type="number" step="0.01" value={form.discount_rate} onChange={(e) => set("discount_rate", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="lease-rate" />
              </div>
              <div>
                <Label>Advance Payment</Label>
                <Input type="number" value={form.advance_payment} onChange={(e) => set("advance_payment", e.target.value)} className="mt-1.5 rounded-sm font-mono" data-testid="lease-advance" />
              </div>
              <div>
                <Label>Payment Timing</Label>
                <Select value={form.payment_timing} onValueChange={(v) => set("payment_timing", v)}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="lease-timing"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="arrears">In Arrears (end of period)</SelectItem>
                    <SelectItem value="advance">In Advance (start of period)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.is_low_value} onCheckedChange={(v) => set("is_low_value", v)} data-testid="lease-lowvalue" />
                <Label className="cursor-pointer">Low-value asset (IFRS 16 exemption)</Label>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 rounded-sm" data-testid="lease-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="rounded-sm" data-testid="save-lease-btn">{editingId ? "Save Changes" : "Create Lease"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 fade-up">
        <div className="w-52">
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="rounded-sm" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lease types</SelectItem>
              {LEASE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-64">
          <Select value={fAsset} onValueChange={setFAsset}>
            <SelectTrigger className="rounded-sm" data-testid="filter-asset"><SelectValue placeholder="All assets" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assets</SelectItem>
              {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_number} — {a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={fNature} onValueChange={setFNature}>
            <SelectTrigger className="rounded-sm" data-testid="filter-nature"><SelectValue placeholder="All natures" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All natures</SelectItem>
              <SelectItem value="Expense">Expense Leases</SelectItem>
              <SelectItem value="Revenue">Revenue Leases</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {leases.length === 0 ? (
        <Card className="rounded-sm border-dashed border-border shadow-none p-16 text-center bg-card fade-up">
          <FileText size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">No leases match. Create a lease contract to see IFRS 16 calculations.</p>
        </Card>
      ) : (
        <Card className="rounded-sm border-border shadow-none overflow-hidden bg-card fade-up">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Lease</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Term</TableHead>
                  <TableHead className="text-right">Rent</TableHead>
                  <TableHead className="text-right">ROU Asset</TableHead>
                  <TableHead className="text-right">Liability</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leases.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer" onClick={() => nav(`/leases/${l.id}`)} data-testid={`lease-row-${l.id}`}>
                    <TableCell>
                      <div className="font-medium">{l.title}</div>
                      <div className="text-xs text-muted-foreground">{l.asset?.asset_number} · {l.party_role}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="rounded-sm">{l.lease_type}</Badge>
                      <Badge variant="outline" className={`rounded-sm ml-1 ${l.lease_nature === "Revenue" ? "text-[hsl(var(--success))] border-[hsl(var(--success))]/40" : "text-amber-600 border-amber-600/40"}`}>{l.lease_nature || "Expense"}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{l.start_date} → {l.end_date}</TableCell>
                    <TableCell className="text-right font-mono">{money(l.rent_amount, l.currency)}<span className="text-muted-foreground text-xs"> /{l.frequency[0]}</span></TableCell>
                    <TableCell className="text-right font-mono text-[hsl(var(--success))]">
                      {l.ifrs.exempt ? <Badge variant="outline" className="rounded-sm gap-1 text-amber-600 border-amber-600/40"><Warning size={12} />Exempt</Badge> : money(l.ifrs.rou_asset, l.currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-destructive">{l.ifrs.exempt ? "—" : money(l.ifrs.lease_liability, l.currency)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(l); }} data-testid={`edit-lease-${l.id}`}
                        className="h-7 w-7 text-muted-foreground hover:text-primary rounded-sm"><PencilSimple size={15} /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
